import { useState, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { Building2, CheckCircle2, ExternalLink, Info, Loader2 } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/frontend/components/ui/tooltip";
import { MedicareConsentDialog } from "@/frontend/components/shared/MedicareConsentDialog";
import { authClient } from "../../auth-client";
import { logoUrl } from "@/frontend/utils/shared";
import { MEDICARE_SOURCE_TYPE, type FhirSource } from "@/frontend/types/fhir";
import { findSandboxCredentials } from "@/frontend/utils/sandbox-credentials";
import type { OrganizationCardOpenDialog } from "@/frontend/types/sandbox-credentials";
import { SandboxCredentialsDialog } from "./SandboxCredentialsDialog";

/**
 * One tile in the patient-portal picker. Real EHRs open the SMART OAuth flow.
 * The Synthetic Hospital tile navigates in-app to `/synthetic-hospital/login`
 * where the patient picks a sample patient and hits Approve — session-only, no OAuth
 * out. Medicare adds a consent dialog, sandbox-credentialed providers add a
 * creds-picker dialog. Only one dialog is ever open at a time.
 */
export function OrganizationCard({
  source,
  isConnected,
  organizationId,
  organizationName,
  organizationIsMedblocks = false,
  patientSessionId,
}: {
  source: FhirSource;
  isConnected: boolean;
  organizationId: string;
  /** Display name of the inviting organization. Surfaced as "Shared with: <name>"
   *  on the connected Medicare card to satisfy CMS BB 3b.2. */
  organizationName?: string;
  /** True only for the Medblocks-owned workspace, where Medblocks is the sole
   *  recipient and the consent disclosure names one party instead of two. */
  organizationIsMedblocks?: boolean;
  /** When set, OAuth callback returns to /?patient_session_id=<patientSessionId> so the
   *  Connect Session UI re-mounts in session mode (Sign out hidden, sticky
   *  Done CTA visible). Without this the SMART plugin defaults to app root
   *  and the patient lands on the post-login screen with no patient_session_id. */
  patientSessionId?: string;
}) {
  const [connecting, setConnecting] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [openDialog, setOpenDialog] = useState<OrganizationCardOpenDialog>(null);

  const isMedicare = source.type === MEDICARE_SOURCE_TYPE;
  const isSyntheticHospital = source.type === "sample";
  const sandboxCreds = findSandboxCredentials(source);

  // Consent and creds share one openDialog slot so only one is ever open.
  const medicareConsentOpen = openDialog === "consent";
  /** Opens or closes the Medicare consent dialog through the shared dialog slot. */
  const setMedicareConsentOpen = (nextOpen: boolean) =>
    setOpenDialog(nextOpen ? "consent" : null);

  // Reset spinner if the browser restores this page from bfcache after the
  // OAuth redirect — otherwise the button stays stuck on "Loading…".
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setConnecting(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  /** Starts the SMART OAuth flow for real EHRs — redirects out to the provider. */
  const startOauth = async () => {
    if (!organizationId) return;
    setConnecting(true);
    try {
      await authClient.link.smart({
        fhirBaseUrl: source.fhirBaseUrl,
        organizationId,
        ...(patientSessionId ? { callbackURL: `/?patient_session_id=${encodeURIComponent(patientSessionId)}` } : {}),
      });
    } catch (error) {
      console.error("Failed to connect:", error);
      setConnecting(false);
    }
  };

  /** Connect click dispatches to whichever dialog fits, or falls through to real-EHR OAuth. Synthetic Hospital uses a plain `<Link>` (see below) so it isn't wired through here. */
  const handleConnect = () => {
    if (isMedicare) { setOpenDialog("consent"); return; }
    if (sandboxCreds) { setOpenDialog("creds"); return; }
    void startOauth();
  };

  const handleMedicareConfirm = () => {
    if (sandboxCreds) { setOpenDialog("creds"); return; }
    setOpenDialog(null);
    void startOauth();
  };

  const handleSandboxProceed = () => {
    setOpenDialog(null);
    void startOauth();
  };

  const showLogo = source.logo && !imgError;

  return (
    <div
      className={`flex items-center gap-2.5 p-2.5 rounded-lg transition-all border ${
        isConnected
          ? "bg-emerald-50 border-emerald-300"
          : "bg-muted/50 border-transparent"
      }`}
    >
      <div className="w-20 h-10 rounded-lg flex items-center justify-center shrink-0 bg-background shadow-sm overflow-hidden border px-1.5">
        {showLogo ? (
          <img
            src={logoUrl(source.logo)!}
            alt="EHR logo"
            className="max-w-full max-h-full object-contain"
            data-ph-no-capture
            onError={() => setImgError(true)}
          />
        ) : (
          <Building2 className="w-5 h-5 text-muted-foreground" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        {/* Wraps in full: two systems sharing a long prefix ellipsize to the
            same string and stop being tellable apart. */}
        <p className="text-sm font-medium break-words leading-snug" data-patient>
          {source.name}
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {!isConnected && isSyntheticHospital && (
          <Button asChild size="sm" className="gap-1 h-7 px-2.5 text-xs">
            <Link to="/synthetic-hospital/login">
              Connect
              <ExternalLink className="w-3 h-3" />
            </Link>
          </Button>
        )}
        {!isConnected && !isSyntheticHospital && (
          <Button
            size="sm"
            onClick={handleConnect}
            disabled={connecting || !organizationId}
            className="gap-1 h-7 px-2.5 text-xs"
          >
            {connecting ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <>
                Connect
                <ExternalLink className="w-3 h-3" />
              </>
            )}
          </Button>
        )}
        {isConnected && (
          <div className="flex items-center gap-1.5 text-emerald-600">
            <span className="text-xs font-medium">Connected</span>
            <CheckCircle2 className="w-4 h-4" />
            {isMedicare && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="Connection details"
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Info className="w-3.5 h-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="max-w-[260px] text-xs">
                    {organizationName && (
                      <p>
                        Shared with <span data-patient className="font-medium">{organizationName}</span>.
                      </p>
                    )}
                    <p>Stored on Medblocks servers until you disconnect.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
      </div>
      {sandboxCreds && (
        <SandboxCredentialsDialog
          open={openDialog === "creds"}
          onOpenChange={(o) => { if (!o) setOpenDialog(null); }}
          creds={sandboxCreds}
          connecting={connecting}
          onProceed={handleSandboxProceed}
        />
      )}
      {isMedicare && (
        <MedicareConsentDialog
          open={medicareConsentOpen}
          onOpenChange={setMedicareConsentOpen}
          recipient={
            organizationIsMedblocks
              ? { kind: "medblocks" }
              : { kind: "invited", organizationName }
          }
          onConfirm={handleMedicareConfirm}
          isBusy={connecting || !organizationId}
        />
      )}
    </div>
  );
}
